"use client";

import type { ReactNode } from "react";
import type { HostingOption } from "@/features/calendar/types/hostingOptions";

import { motion } from "motion/react";
import { addMinutes, format, parse, addDays } from "date-fns";
import { Icon } from "@iconify-icon/react";

import { Badge } from "@/components/ui/badge";
import { LessonHost } from "@/features/calendar/types/create-slot";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Animation constants
export const slideUpAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0 },
};

export const staggeredContainerAnimation = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      delayChildren: 0.1,
    },
  },
  exit: { opacity: 0 },
};

// Status for slot creation
export type SlotStatus = "pending" | "success" | "error";

// Get hosting method name
export const getHostingMethodNameAndIcon = (
  hostMethod: string,
  hostingOptions: HostingOption[],
): { name: string; icon: ReactNode } => {
  const option = hostingOptions.find((opt) => opt.id === hostMethod);

  return option
    ? { name: option.title, icon: option.icon }
    : {
        name: hostMethod,
        icon: <Icon height={"100%"} icon={"mdi:question-mark-circle-outline"} width={"100%"} />,
      };
};

// Format the total time in hours and minutes
export const formatTotalTime = (totalMinutes: number, t: any): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} ${t("calendar:templates.minutes")}`;
  } else if (minutes === 0) {
    return `${hours} ${hours === 1 ? t("calendar:templates.hour") : t("calendar:templates.hours")}`;
  } else {
    return `${hours} ${hours === 1 ? t("calendar:templates.hour") : t("calendar:templates.hours")} ${minutes} ${t("calendar:templates.minutes")}`;
  }
};

// Calculate the total time for all slots
export const calculateTotalTime = (
  startTime: string,
  endTime: string,
  numberOfSlots: number | undefined,
  breakMinutes: number | undefined,
): number => {
  if (!startTime || !endTime || numberOfSlots === undefined) return 0;

  // Parse times
  const startDate = parse(startTime, "HH:mm", new Date());
  const endDate = parse(endTime, "HH:mm", new Date());

  // Calculate duration of a single slot
  let slotDurationMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);

  if (slotDurationMinutes < 0) {
    slotDurationMinutes += 24 * 60; // Add 24 hours in minutes
  }

  // Calculate total time including breaks
  const breakMins = breakMinutes ?? 0;
  const totalMinutes = slotDurationMinutes * numberOfSlots + breakMins * (numberOfSlots - 1);

  return totalMinutes;
};

// Render hosting options
export const renderHostingOptions = (
  hostingOptions: HostingOption[],
  selectedHost: string,
  onSelect: (value: string) => void,
  t: any,
): ReactNode => {
  return hostingOptions.map((option, index) => {
    const isSelected = selectedHost === option.id;
    const isDisabled = Boolean(option.disabled);

    const card = (
      <motion.div
        key={`host-option-${option.id}`}
        animate={{ opacity: 1 }}
        aria-disabled={isDisabled}
        className={`relative rounded-md p-4 transition-all ${
          isDisabled
            ? "bg-muted/30 cursor-not-allowed opacity-70"
            : isSelected
              ? "bg-muted cursor-pointer shadow-md"
              : "bg-muted/50 hover:bg-side-accent cursor-pointer"
        }`}
        initial={{ opacity: 0 }}
        transition={{ delay: index * 0.1 }}
        onClick={() => {
          if (isDisabled) {
            return;
          }
          onSelect(option.id);
        }}
      >
        <div className="m-0 flex items-center gap-3">
          <div
            className={`text-muted-foreground border-muted-foreground relative ${
              isSelected &&
              !isDisabled &&
              "bg-accent-foreground border-accent-foreground box-border"
            } size-4 min-w-4 rounded-full border`}
          />
          <div className="relative m-0 flex w-full flex-col text-start font-normal">
            <div className="flex items-center gap-3">
              <div
                className={`bg-muted flex size-10 items-center justify-center rounded-lg p-2 ${isDisabled ? "opacity-70" : ""}`}
              >
                {option.icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-base font-medium ${isDisabled ? "text-muted-foreground" : "text-accent-foreground"}`}
                  >
                    {option.title}
                  </span>
                  {option.id === LessonHost.Ximly && (
                    <Badge className="bg-accent-foreground text-primary absolute top-2 right-2">
                      {t("calendar:templates.recommended")}
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-sm">{option.description}</p>
                {option.id === LessonHost.Ximly && (
                  <p className="text-muted-foreground mt-1 text-xs italic">
                    {t("calendar:templates.additionalFee", {
                      fee: "0.36$/hour",
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        {isDisabled && (
          <div className="bg-background/60 pointer-events-none absolute inset-0 rounded-md" />
        )}
      </motion.div>
    );

    if (option.hint && isDisabled) {
      return (
        <TooltipProvider key={`host-option-${option.id}`}>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>{card}</TooltipTrigger>
            <TooltipContent className="max-w-xs text-left">
              <p className="text-accent-foreground text-sm font-medium">{option.hint.title}</p>
              <p className="text-muted-foreground mt-1 text-xs">{option.hint.description}</p>
              <a
                className="mt-2 inline-flex items-center gap-1 text-xs text-blue-400 underline"
                href={option.hint.actionHref}
              >
                <span>{option.hint.actionLabel}</span>
                <Icon height={"1rem"} icon="tabler:arrow-up-right" width={"1rem"} />
              </a>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return card;
  });
};

// Generate slots based on input data
export interface BaseSlotData {
  start_time: string;
  end_time: string;
  lesson_host: string;
  communication_details?: string;
}

export interface DateSlotData extends BaseSlotData {
  date: string;
  end_date: string;
}

export interface WeeklySlotData extends BaseSlotData {
  day_of_week?: number;
}

export interface PreviewSlot extends BaseSlotData {
  id: string;
  status: SlotStatus;
  error?: string;
  errorType?: string;
  date?: string;
  end_date?: string;
  day_of_week?: number;
  overlap_gc?: boolean;
}

// Generate preview slots for date-based slots
export const generateDatePreviewSlots = (
  data: DateSlotData,
  createMultiple: boolean,
  numberOfSlots: number | undefined,
  breakMinutes: number | undefined,
): PreviewSlot[] => {
  const slots: PreviewSlot[] = [];
  const numSlots = createMultiple ? (numberOfSlots ?? 1) : 1;
  const breakMins = breakMinutes ?? 0;

  // Parse the original date
  const originalDate = parse(data.date, "yyyy-MM-dd", new Date());

  // Parse the original times
  let prevStartTime = parse(data.start_time, "HH:mm", new Date());
  let currentDate = originalDate;
  let daysToAdd = 0;

  for (let i = 0; i < numSlots; i++) {
    // Clone the data for this slot
    const slotData = { ...data };

    if (i === 0) {
      // First slot uses the original data
      slots.push({
        ...slotData,
        id: `preview-slot-${i}`,
        status: "pending",
      });
      continue;
    }

    // Parse the original times for each iteration
    const startTime = parse(data.start_time, "HH:mm", new Date());
    const endTime = parse(data.end_time, "HH:mm", new Date());

    // Calculate the slot duration
    let slotDurationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

    if (slotDurationMinutes < 0) {
      slotDurationMinutes += 24 * 60; // Add 24 hours in minutes
    }

    // Calculate new start time by adding (slot duration + break) to the previous slot's start time
    const newStartTime = addMinutes(prevStartTime, slotDurationMinutes + breakMins);

    // Calculate new end time based on the new start time
    const newEndTime = addMinutes(newStartTime, slotDurationMinutes);

    // Format the times back to HH:mm
    const newStartTimeStr = format(newStartTime, "HH:mm");
    const newEndTimeStr = format(newEndTime, "HH:mm");

    // Check if we need to increment the date by comparing with previous start time
    if (
      newStartTime.getHours() < prevStartTime.getHours() ||
      (newStartTime.getHours() === prevStartTime.getHours() &&
        newStartTime.getMinutes() < prevStartTime.getMinutes())
    ) {
      daysToAdd++;
      currentDate = addMinutes(originalDate, 24 * 60 * daysToAdd);
    }

    slotData.start_time = newStartTimeStr;
    slotData.end_time = newEndTimeStr;
    slotData.date = format(currentDate, "yyyy-MM-dd");
    slotData.end_date = format(currentDate, "yyyy-MM-dd");

    // Update the previous start time for the next iteration
    prevStartTime = newStartTime;

    slots.push({
      ...slotData,
      id: `preview-slot-${i}`,
      status: "pending",
    });
  }

  return slots;
};

// Generate preview slots for weekly template slots
export const generateWeeklyPreviewSlots = (
  data: WeeklySlotData,
  createMultiple: boolean,
  numberOfSlots: number | undefined,
  breakMinutes: number | undefined,
): PreviewSlot[] => {
  const slots: PreviewSlot[] = [];
  const numSlots = createMultiple ? (numberOfSlots ?? 1) : 1;
  const breakMins = breakMinutes ?? 0;
  let isOverflowing = 0;
  let initialDayStartTime = data.start_time.split(":").map(Number)[0];

  for (let i = 0; i < numSlots; i++) {
    // Clone the data for this slot
    const slotData = { ...data };

    // Only modify start and end times for slots after the first one
    if (i > 0) {
      // Parse the original times
      const startTime = parse(data.start_time, "HH:mm", new Date());
      const endTime = parse(data.end_time, "HH:mm", new Date());

      // Calculate the slot duration
      let slotDurationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

      if (slotDurationMinutes < 0) {
        slotDurationMinutes += 24 * 60; // Add 24 hours in minutes
      }

      // Calculate new start time by adding (slot duration + break) * i minutes to the original start time
      const newStartTime = addMinutes(startTime, (slotDurationMinutes + breakMins) * i);

      // Calculate new end time based on the new start time
      const newEndTime = addMinutes(newStartTime, slotDurationMinutes);

      // Format the times back to HH:mm
      slotData.start_time = format(newStartTime, "HH:mm");
      slotData.end_time = format(newEndTime, "HH:mm");

      // Handle day overflow for weekly templates
      if (slotData.day_of_week) {
        // Check if the end time is on the next day (if start hour > end hour)
        const [startHour] = slotData.start_time.split(":").map(Number);

        // If the end time is earlier than the start time, it means we've crossed midnight
        if (startHour <= initialDayStartTime) {
          initialDayStartTime = startHour;
          isOverflowing++;
        }
        if (isOverflowing > 0) {
          slotData.day_of_week = (slotData.day_of_week + isOverflowing) % 7 || 7;
        }
      }
    }

    slots.push({
      ...slotData,
      id: `preview-slot-${i}`,
      status: "pending",
    });
  }

  return slots;
};

// Generate preview slots for timeframe mode (fills a time range with slots)
export const generateTimeframePreviewSlots = (
  data: WeeklySlotData,
  timeframeEnd: string,
  breakMinutes: number | undefined,
  manualSlotDurationMinutes?: number,
): PreviewSlot[] => {
  const slots: PreviewSlot[] = [];
  const breakMins = breakMinutes ?? 0;

  if (manualSlotDurationMinutes && manualSlotDurationMinutes < 10) {
    return [];
  }

  // Parse slot times
  const slotStartTime = parse(data.start_time, "HH:mm", new Date());

  // Calculate slot duration
  let slotDurationMinutes = manualSlotDurationMinutes;

  if (!slotDurationMinutes) {
    // In timeframe mode, we should require a manual slot duration
    // Only fall back to form data if we're not in timeframe mode
    const slotEndTime = parse(data.end_time, "HH:mm", new Date());

    slotDurationMinutes = (slotEndTime.getTime() - slotStartTime.getTime()) / (1000 * 60);

    if (slotDurationMinutes < 0) {
      slotDurationMinutes += 24 * 60; // Handle overnight slots
    }
  }

  if (slotDurationMinutes < 10) {
    return [];
  }

  // If timeframeEnd is the same as the slot end time, default to one hour after the end time
  let effectiveTimeframeEnd = timeframeEnd;

  if (timeframeEnd === data.end_time) {
    const oneHourLater = addMinutes(slotStartTime, 60);

    effectiveTimeframeEnd = format(oneHourLater, "HH:mm");
  }

  // Parse timeframe start and end
  const timeframeStartTime = parse(data.start_time, "HH:mm", new Date());
  const timeframeEndTime = parse(effectiveTimeframeEnd, "HH:mm", new Date());

  // Calculate timeframe duration (handle overnight timeframes)
  let timeframeDurationMinutes =
    (timeframeEndTime.getTime() - timeframeStartTime.getTime()) / (1000 * 60);

  if (timeframeDurationMinutes <= 0) {
    timeframeDurationMinutes += 24 * 60; // Handle overnight timeframes
  }

  // Cap at 24 hours
  timeframeDurationMinutes = Math.min(timeframeDurationMinutes, 24 * 60);

  // Calculate how many slots can fit in the timeframe
  const totalSlotAndBreakTime = slotDurationMinutes + breakMins;
  let numSlots = 0;

  if (timeframeDurationMinutes >= slotDurationMinutes) {
    const completeCombos = Math.floor(timeframeDurationMinutes / totalSlotAndBreakTime);
    const remainingTime = timeframeDurationMinutes - completeCombos * totalSlotAndBreakTime;

    numSlots = completeCombos + (remainingTime >= slotDurationMinutes ? 1 : 0);
  }

  // Cap at 20 slots maximum
  numSlots = Math.min(numSlots, 20);

  let currentStartTime = timeframeStartTime;
  let currentDay = data.day_of_week || 1;
  let dayOffset = 0;

  for (let i = 0; i < numSlots; i++) {
    // Calculate end time for this slot
    const currentEndTime = addMinutes(currentStartTime, slotDurationMinutes);

    // Check if we've crossed midnight
    const slotStartHour = currentStartTime.getHours();
    const previousSlotStartHour =
      i > 0
        ? parse(slots[i - 1].start_time, "HH:mm", new Date()).getHours()
        : data.start_time.split(":").map(Number)[0];

    if (slotStartHour < Number(previousSlotStartHour)) {
      dayOffset++;
      if (data.day_of_week) {
        currentDay = ((data.day_of_week - 1 + dayOffset) % 7) + 1;
      }
    }

    // Create the slot
    const slot: PreviewSlot = {
      ...data,
      id: `preview-${i}`,
      start_time: format(currentStartTime, "HH:mm"),
      end_time: format(currentEndTime, "HH:mm"),
      day_of_week: data.day_of_week ? currentDay : undefined,
      status: "pending",
    };

    slots.push(slot);

    // Move to next slot start time (current end time + break)
    currentStartTime = addMinutes(currentEndTime, breakMins);
  }

  return slots;
};

// Calculate how many slots can fit in a timeframe
export const calculateSlotsInTimeframe = (
  startTime: string,
  endTime: string,
  timeframeEnd: string,
  breakMinutes: number | undefined,
  manualSlotDurationMinutes?: number,
): number => {
  if (!startTime || !timeframeEnd) return 0;

  // Validate slot duration minimum
  if (manualSlotDurationMinutes !== undefined && manualSlotDurationMinutes < 10) {
    return 0; // Return 0 if slot duration is less than 10 minutes
  }

  // Parse times
  const timeframeStartTime = parse(startTime, "HH:mm", new Date());
  const timeframeEndTime = parse(timeframeEnd, "HH:mm", new Date());

  // Calculate slot duration - use manual override if provided, otherwise use form data
  let slotDurationMinutes = manualSlotDurationMinutes;

  if (slotDurationMinutes === undefined && endTime) {
    const slotEndTime = parse(endTime, "HH:mm", new Date());

    slotDurationMinutes = (slotEndTime.getTime() - timeframeStartTime.getTime()) / (1000 * 60);
    if (slotDurationMinutes < 0) {
      slotDurationMinutes += 24 * 60; // Handle overnight slots
    }
  }

  // Default to 60 minutes if no duration is specified
  if (slotDurationMinutes === undefined) {
    slotDurationMinutes = 60;
  }

  // Validate minimum slot duration
  if (slotDurationMinutes < 10) {
    return 0; // Return 0 if slot duration is less than 10 minutes
  }

  // Calculate timeframe duration - handle overnight timeframes properly
  let timeframeDurationMinutes =
    (timeframeEndTime.getTime() - timeframeStartTime.getTime()) / (1000 * 60);

  // If end time is before start time, it means it overflows to the next day
  if (timeframeDurationMinutes <= 0) {
    timeframeDurationMinutes += 24 * 60;
  }

  // Cap at 24 hours
  timeframeDurationMinutes = Math.min(timeframeDurationMinutes, 24 * 60);

  const breakMins = breakMinutes ?? 0;

  // Calculate maximum number of slots that can fit
  const totalSlotAndBreakTime = slotDurationMinutes + breakMins;
  let maxSlots = 0;

  if (timeframeDurationMinutes >= slotDurationMinutes) {
    // Calculate how many complete slot+break combinations fit
    const completeSlotBreakCombos = Math.floor(timeframeDurationMinutes / totalSlotAndBreakTime);

    // Check if we can fit one more slot without break at the end
    const remainingTime =
      timeframeDurationMinutes - completeSlotBreakCombos * totalSlotAndBreakTime;
    const canFitOneMore = remainingTime >= slotDurationMinutes;

    maxSlots = completeSlotBreakCombos + (canFitOneMore ? 1 : 0);
  }

  // Cap at 20 slots maximum for performance
  return Math.min(maxSlots, 20);
};

// Calculate the duration of a timeframe in minutes
export const calculateTimeframeDuration = (startTime: string, timeframeEnd: string): number => {
  if (!startTime || !timeframeEnd) return 0;

  const start = parse(startTime, "HH:mm", new Date());
  const end = parse(timeframeEnd, "HH:mm", new Date());

  let durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

  if (durationMinutes <= 0) {
    durationMinutes += 24 * 60;
  }

  return Math.min(durationMinutes, 24 * 60);
};

// Generate preview slots for date-based timeframe mode (for create-slot-modal)
export const generateDateTimeframePreviewSlots = (
  data: DateSlotData,
  timeframeEnd: string,
  breakMinutes: number | undefined,
  manualSlotDurationMinutes?: number,
): PreviewSlot[] => {
  const slots: PreviewSlot[] = [];
  const breakMins = breakMinutes ?? 0;

  // Validate slot duration minimum
  if (manualSlotDurationMinutes !== undefined && manualSlotDurationMinutes < 10) {
    return []; // Return empty array if slot duration is less than 10 minutes
  }

  // Parse times and dates
  const baseDate = parse(data.date, "yyyy-MM-dd", new Date());
  const timeframeStartTime = parse(data.start_time, "HH:mm", new Date());
  const timeframeEndTime = parse(timeframeEnd, "HH:mm", new Date());

  // Calculate slot duration - use manual override if provided, otherwise use form data
  let slotDurationMinutes = manualSlotDurationMinutes;

  if (slotDurationMinutes === undefined) {
    // In timeframe mode, we should require a manual slot duration
    // Only fall back to form data if we're not in timeframe mode
    // For timeframe mode, the data.end_time represents the end of the timeframe, not individual slot duration
    const slotEndTime = parse(data.end_time, "HH:mm", new Date());

    slotDurationMinutes = (slotEndTime.getTime() - timeframeStartTime.getTime()) / (1000 * 60);
    if (slotDurationMinutes < 0) {
      slotDurationMinutes += 24 * 60; // Handle overnight slots
    }
  }

  // Validate minimum slot duration
  if (slotDurationMinutes < 10) {
    return []; // Return empty array if slot duration is less than 10 minutes
  }

  // Calculate timeframe duration - handle overnight timeframes properly
  let timeframeDurationMinutes =
    (timeframeEndTime.getTime() - timeframeStartTime.getTime()) / (1000 * 60);

  // If end time is before start time, it means it overflows to the next day
  if (timeframeDurationMinutes <= 0) {
    timeframeDurationMinutes += 24 * 60;
  }

  // Cap at 24 hours maximum
  timeframeDurationMinutes = Math.min(timeframeDurationMinutes, 24 * 60);

  // Calculate maximum number of slots that can fit
  // We need to account for breaks between slots, but not after the last slot
  const totalSlotAndBreakTime = slotDurationMinutes + breakMins;
  let maxSlots = 0;

  if (timeframeDurationMinutes >= slotDurationMinutes) {
    // Calculate how many complete slot+break combinations fit
    const completeSlotBreakCombos = Math.floor(timeframeDurationMinutes / totalSlotAndBreakTime);

    // Check if we can fit one more slot without break at the end
    const remainingTime =
      timeframeDurationMinutes - completeSlotBreakCombos * totalSlotAndBreakTime;
    const canFitOneMore = remainingTime >= slotDurationMinutes;

    maxSlots = completeSlotBreakCombos + (canFitOneMore ? 1 : 0);
  }

  // Cap at 20 slots maximum for performance
  maxSlots = Math.min(maxSlots, 20);

  // Generate slots
  let currentStartTime = timeframeStartTime;
  let currentDate = baseDate;

  // Determine if timeframe spans to next day
  const timeframeSpansNextDay = timeframeEndTime.getTime() < timeframeStartTime.getTime();
  let timeframeEndDate = baseDate;

  if (timeframeSpansNextDay) {
    timeframeEndDate = addDays(baseDate, 1);
  }

  for (let i = 0; i < maxSlots; i++) {
    // Calculate end time for this slot
    const currentEndTime = addMinutes(currentStartTime, slotDurationMinutes);

    // Check if we've crossed midnight during slot generation
    if (currentStartTime.getHours() === 0 && i > 0) {
      // If we're at midnight and it's not the first slot, we've moved to next day
      const prevSlotTime = addMinutes(currentStartTime, -(slotDurationMinutes + breakMins));

      if (prevSlotTime.getHours() > currentStartTime.getHours()) {
        currentDate = addDays(currentDate, 1);
      }
    }

    // Determine end date for this slot (might be next day if slot crosses midnight)
    let endDate = currentDate;

    if (currentEndTime.getHours() < currentStartTime.getHours()) {
      endDate = addDays(currentDate, 1);
    }

    // Check if this slot would end beyond our timeframe
    const slotEndDateTime = new Date(endDate);

    slotEndDateTime.setHours(currentEndTime.getHours(), currentEndTime.getMinutes(), 0, 0);

    const timeframeEndDateTime = new Date(timeframeEndDate);

    timeframeEndDateTime.setHours(timeframeEndTime.getHours(), timeframeEndTime.getMinutes(), 0, 0);

    // Stop if this slot would end after the timeframe
    if (slotEndDateTime > timeframeEndDateTime) {
      break;
    }

    // Create the slot
    const slot: PreviewSlot = {
      ...data,
      id: `preview-${i}`,
      start_time: format(currentStartTime, "HH:mm"),
      end_time: format(currentEndTime, "HH:mm"),
      date: format(currentDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      status: "pending",
    };

    slots.push(slot);

    // Calculate next slot start time (current end time + break)
    currentStartTime = addMinutes(currentEndTime, breakMins);

    // Check if we need to move to next day after adding break
    if (currentStartTime.getDate() !== currentEndTime.getDate()) {
      currentDate = addDays(currentDate, 1);
    }
  }

  return slots;
};
